import {
  PostDefiYieldRedeemConfirmResponse,
  PostDefiYieldRedeemPreviewResponse,
} from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage, type MessageKey } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { CurrencyDisplay } from '../../../misc/display';
import { getEstimatedHours } from '../date';

export type RedeemResult =
  | {
      type: 'success';
      previewResult: PostDefiYieldRedeemPreviewResponse;
      confirmResult: PostDefiYieldRedeemConfirmResponse;
    }
  | { type: 'error'; message: string };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  result: RedeemResult;
  fromLabelId: MessageKey;
}

export const RedeemResultModal = ({
  isOpen,
  onClose,
  onExit,
  result,
  fromLabelId = 'Earn.r260601.From_narsil_usdt_prime',
}: Props) => {
  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      {result.type === 'success' && (
        <SuccessContent
          previewResult={result.previewResult}
          confirmResult={result.confirmResult}
          onClose={onClose}
          fromLabelId={fromLabelId}
        />
      )}
      {result.type === 'error' && <ErrorContent message={result.message} onClose={onClose} />}
    </Modal.Root>
  );
};

const SuccessContent = ({
  previewResult,
  confirmResult,
  onClose,
  fromLabelId,
}: {
  previewResult: PostDefiYieldRedeemPreviewResponse;
  confirmResult: PostDefiYieldRedeemConfirmResponse;
  onClose: () => void;
  fromLabelId: MessageKey;
}) => {
  const isStandard = confirmResult.type === 'STANDARD';

  return (
    <>
      <Modal.Content>
        <div className={stack({ gap: 32 })}>
          <div className={vstack({ gap: 8 })}>
            <CheckCircleIcon size={48} color={token('colors.content.accent.info')} />
            <p className={css({ textStyle: 'Title/L', color: 'content.default.level-5', textAlign: 'center' })}>
              {isStandard ? (
                <FormattedMessage id='Redeem.r251008.Redemption_request_submitted' />
              ) : (
                <FormattedMessage id='Redeem.r260108.Redemption_successful' />
              )}
            </p>
          </div>
          <div className={stack({ gap: 10 })}>
            <div
              className={stack({
                gap: 16,
                py: 16,
                px: 20,
                rounded: 12,
                bg: 'background.default.level-1',
              })}
            >
              <div className={vstack({ gap: 2 })}>
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                  {isStandard ? (
                    <FormattedMessage id='Earn.r251204.You_redeem' />
                  ) : (
                    <FormattedMessage id='Earn.r260108.You_redeemed' />
                  )}
                </p>
                <CurrencyDisplay
                  currency={UsdtCurrency}
                  icon={<CurrencyDisplay.Icon size={24} />}
                  mainLabel={
                    <p className={css({ textStyle: 'Title/M/Monospace', color: 'content.default.level-5' })}>
                      <Formatted.Number
                        value={
                          isStandard
                            ? previewResult.toAmount
                            : Num(previewResult.toAmount).multipliedBy(Num(1).minus(Num(previewResult.feeRate)))
                        }
                        suffix={UsdtCurrency}
                        precision={UsdtPrecision}
                        toFixed
                      />
                    </p>
                  }
                />
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                  {/* @ts-expect-error — fromLabelId keys do not require values */}
                  <FormattedMessage id={fromLabelId} />
                </p>
              </div>
              <div
                className={stack({
                  gap: 8,
                  pt: 16,
                  borderColor: 'border.default.level-1',
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                })}
              >
                {isStandard && (
                  <div className={hstack({ justify: 'space-between' })}>
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <FormattedMessage id='Earn.r260304.You_receive' />
                    </span>
                    <span className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage id='Earn.r251204.Determined_at_execution_value' />
                    </span>
                  </div>
                )}
                <div className={hstack({ justify: 'space-between' })}>
                  <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                    <FormattedMessage id='Earn.r260304.Shares' />
                  </span>
                  <span className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>
                    <Formatted.Number value={confirmResult.shareTokenAmount} precision={6} toFixed />
                  </span>
                </div>
                {isStandard && (
                  <div className={hstack({ justify: 'space-between' })}>
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
                    </span>
                    <span className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage
                        id='Earn.r251204.Within_hours_duration'
                        values={{ hour: getEstimatedHours(confirmResult.estimatedCompletedAt) }}
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isStandard && (
              <ul
                className={stack({
                  gap: 4,
                  list: 'disc',
                  textStyle: 'Text/S/Regular',
                  color: 'content.default.level-3',
                })}
              >
                <li>
                  <FormattedMessage id='Earn.r251204.Redemption_amount_variance_notice_with_fees' />
                </li>
                <li>
                  <FormattedMessage id='Earn.r260108.Processing_notification_notice' />
                </li>
              </ul>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<FormattedMessage id='Common.r250401.Done' />} onClick={onClose} />
      </Modal.Footer>
    </>
  );
};

const ErrorContent = ({ message, onClose }: { message: string; onClose: () => void }) => {
  return (
    <>
      <Modal.Content>
        <div className={stack({ gap: 32 })}>
          <div className={vstack({ gap: 8 })}>
            <CautionCircleIcon size={48} color={token('colors.content.state.negative')} />
            <p className={css({ textStyle: 'Title/L', color: 'content.default.level-5', textAlign: 'center' })}>
              <FormattedMessage id='Common.r250401.Error' />
            </p>
          </div>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', textAlign: 'center' })}>
            {message}
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<FormattedMessage id='Common.r250401.Done' />} onClick={onClose} />
      </Modal.Footer>
    </>
  );
};
